/**
 * Theme application (theming.md §2). Two jobs, both on :root of THIS document (the iframe owns
 * its own :root — ADR-0004 — so nothing leaks in or out):
 *
 *  - applyMode(mode)   → Layer 3: toggles the light/dark ramp via [data-theme].
 *  - applyTenant(cfg)  → Layer 2: sets ONLY the whitelisted accent + font props from the town's
 *                        embed-config. Never touches neutrals/spacing/status/focus (a11y guarantees).
 *
 * Fonts: `cfg.fonts.display`/`.body` are whatever the tenant saved — a bare family name, a free-text
 * CSS stack, or unset. Callers resolve that to an actually-renderable stack via the
 * `useBrandFontStacks` hook (theme/fonts.ts) — a curated self-hosted (Fontsource) webfont when the
 * family matches our registry, or a system-font stack otherwise — and pass the RESOLVED stacks in
 * here. applyTenant/residentThemeVars stay synchronous and only ever write a stack that's actually
 * loaded (the hook itself starts callers at a system stack, so there's no window where a font var
 * names an unloaded webfont).
 */
import type { AccentRamp, EmbedConfig, ThemeMode } from "./embedConfig.js";
import type { BrandFontStacks } from "./fonts.js";
import { deriveResidentPalette } from "./contrast.js";

export function applyMode(mode: ThemeMode, persist = true): void {
  document.documentElement.setAttribute("data-theme", mode);
  if (persist) safeSetStored(mode);
}

function accentVarsFor(ramp: AccentRamp): Record<string, string> {
  return {
    "--td-accent": ramp.base,
    "--td-accent-hover": ramp.hover,
    "--td-accent-active": ramp.active,
    "--td-accent-weak": ramp.weak,
    "--td-accent-contrast": ramp.contrast,
    // The focus ring tracks the accent so the town's brand color is the focus color.
    "--td-focus-ring": ramp.base,
  };
}

/**
 * Apply the town override. Only the whitelist (accent ramp + fonts) is written; the correct
 * light/dark accent is chosen from the current mode so a mode toggle re-applies cleanly. `fontStacks`
 * is the already-resolved (self-hosted-or-system) pair from `useBrandFontStacks` — always written,
 * so :root never falls back to the CSS-file default while a font is loading.
 */
export function applyTenant(cfg: EmbedConfig, mode: ThemeMode, fontStacks: BrandFontStacks): void {
  const root = document.documentElement;
  const ramp = mode === "dark" ? cfg.accent.dark : cfg.accent.light;
  for (const [k, v] of Object.entries(accentVarsFor(ramp))) root.style.setProperty(k, v);
  root.style.setProperty("--td-font-display", fontStacks.display);
  root.style.setProperty("--td-font-body", fontStacks.body);
}

/**
 * RESIDENT forms (theming.md §2, "branded, no toggle"): build the inline CSS custom properties
 * scoped to the resident-form wrapper (see components/ResidentThemeRoot.tsx). Unlike applyTenant
 * (staff console, writes the accent/fonts to :root), this NEVER writes to :root — it returns a style
 * object the resident page's own wrapper `<div>` applies, so its derived neutrals stay scoped to the
 * resident form and never leak into / are leaked on by the console's :root vars, even though both
 * mount under one shared ThemeProvider. (MVP has no light/dark toggle on either side.)
 *
 * Page background = cfg.backgroundColor (town's site); accent = cfg.accent.light (residents get
 * ONE look, always the light/brand accent ramp — there's no dark accent to switch to without a
 * toggle); card/surface + text/ink are DERIVED from the background so contrast stays AA (theme/
 * contrast.ts deriveResidentPalette) — pulled colors can never render unreadable text. `fontStacks`
 * is the already-resolved (self-hosted-or-system) pair from `useBrandFontStacks`.
 */
export function residentThemeVars(cfg: EmbedConfig, fontStacks: BrandFontStacks): Record<string, string> {
  const palette = deriveResidentPalette(cfg.backgroundColor);
  const ramp = cfg.accent.light;
  return {
    "--td-paper": palette.bg,
    "--td-surface": palette.surface,
    "--td-surface-2": palette.surface,
    "--td-ink": palette.ink,
    "--td-ink-2": palette.ink2,
    "--td-ink-3": palette.ink3,
    "--td-ink-invert": palette.bg,
    "--td-border": palette.border,
    "--td-border-strong": palette.borderStrong,
    ...accentVarsFor(ramp),
    "--td-focus-offset": palette.bg,
    "--td-font-display": fontStacks.display,
    "--td-font-body": fontStacks.body,
  };
}

function safeSetStored(mode: ThemeMode): void {
  try {
    window.localStorage.setItem("td-theme", mode);
  } catch {
    /* storage blocked (private mode / sandboxed iframe) — mode still applies for the session */
  }
}
